import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Input } from "@/app/global-components/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/global-components/form";
import { Link } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { TextField } from "@mui/material";
import { routes } from "@/consts";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface LoginFormFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  multiline?: boolean;
  placeholder: string;
  description?: string;
  inputType?: string;
  formControl: Control<T>;
}

const LoginFieldView = <T extends FieldValues>({
  name,
  label,
  multiline,
  placeholder,
  inputType,
  formControl,
}: LoginFormFieldProps<T>) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [showPassword, setShowPassword] = useState(false);
  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem className="mb-4">
          {name === "password" ? (
            <div className="flex flex-col xs:flex-row justify-between items-center mb-2">
              <FormLabel className="mb-2 sm:mb-0">{label}</FormLabel>
              <p className="text-sm text-textcolor-mediumGray">
                <span className="underline text-textcolor-black cursor-pointer">
                  <Link to={routes.forgetPassword}>
                    {t("authentication.forgotPassword.title")}
                  </Link>
                </span>
              </p>
            </div>
          ) : (
            <FormLabel className="mb-2">{label}</FormLabel>
          )}

          <FormControl>
            {multiline ? (
              <TextField
                multiline={multiline}
                rows={4}
                className="!bg-background"
                sx={{ display: "flex", flexDirection: "column" }}
                {...field}
              />
            ) : (
              <div className="relative">
                <Input
                  placeholder={placeholder}
                  type={
                    inputType === "password" && showPassword
                      ? "text"
                      : inputType || "text"
                  }
                  {...field}
                />
                {inputType === "password" && (
                  <button
                    type="button"
                    className={cn("absolute top-1/2 -translate-y-1/2",locale === "en" ? "right-3" : "left-3")}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaRegEye className="h-5 w-5 text-gray-500" />
                    ) : (
                      <FaRegEyeSlash className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                )}
              </div>
            )}
          </FormControl>
          <FormMessage className="text-red-500 text-xs" />
        </FormItem>
      )}
    />
  );
};

export default LoginFieldView;
